import { Box, Portal, Stack, SxProps, Theme, Tooltip } from '@mui/material';
import React, { ReactNode, useContext } from 'react';

import { QuoteFooterContext } from './QuoteEditorSurface';
import type { QuoteEditorLifecycle } from './QuoteEditorSurface';

function disabledReason(node: ReactNode, fallback: string): string {
  if (!React.isValidElement(node)) return '';
  const props = node.props as { disabled?: boolean; title?: ReactNode; children?: ReactNode, useContext };
  if (props.disabled) return typeof props.title === 'string' && props.title ? props.title : fallback;
  let reason = '';
  React.Children.forEach(props.children, child => {
    reason ||= disabledReason(child, typeof props.title === 'string' && props.title ? props.title : fallback);
  });
  return reason;
}

interface QuoteEditorActionBarProps {
  discard: ReactNode;
  deposit: ReactNode;
  adminFee: ReactNode;
  terms: ReactNode;
  save: ReactNode;
  saveStatus?: ReactNode;
  approve: ReactNode;
  compose: ReactNode;
  lifecycle: QuoteEditorLifecycle;
  sx?: SxProps<Theme>;
}

/**
 * Authoritative quote action rail. Route controllers supply capabilities, but
 * the controls keep the same order and layout before and after the quote has
 * an id. Unavailable capabilities should be supplied as disabled controls.
 */
export default function QuoteEditorActionBar({
  deposit,
  approve,
  compose,
  lifecycle,
  sx
}: QuoteEditorActionBarProps) {
  const { container } = useContext(QuoteFooterContext);
  const styles = [
    {
      '& [data-quote-action="compose"] button.MuiButton-root': { width: 'max-content', minWidth: 150, whiteSpace: 'nowrap' },
      '& button.MuiButton-root': {
        height: 34,
        minHeight: 34,
        borderRadius: '8px',
        py: 0
      }
    },
    ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
  ] as SxProps<Theme>;

  return (
    <Portal container={container} disablePortal={!container}>
    <Box
      data-testid="shared-quote-editor-actions"
      data-quote-lifecycle={lifecycle}
      sx={styles}
    >
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1}
        sx={{ mt: 0 }}
      >
        <Tooltip arrow title={disabledReason(deposit, 'Save and approve the quote before creating a deposit.')}>
          <Box data-quote-action="deposit">{deposit}</Box>
        </Tooltip>
        <Tooltip arrow title={disabledReason(approve, 'Send the current quote revision before approving it.')}>
          <Box data-quote-action="approve">{approve}</Box>
        </Tooltip>
        <Tooltip arrow title={disabledReason(compose, 'Complete the required quote details before composing.')}>
          <Box data-quote-action="compose">{compose}</Box>
        </Tooltip>
      </Stack>
    </Box>
    </Portal>
  );
}
